<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import UrlCheckInput from './UrlCheckInput.vue';
import OnboardingStepLayout from './OnboardingStepLayout.vue';
import OnboardingExtractionProgress from './OnboardingExtractionProgress.vue';
import { ONBOARDING_NAV_KEY } from '../useOnboardingNav';

const store = useStore();
const { t } = useI18n();
const nav = inject(ONBOARDING_NAV_KEY, null);

const spacesUrl = ref(store.state.onboarding.domain || '');
const urlIsValid = ref(false);
const view = ref('form');

const uiFlags = computed(() => store.state.onboarding.uiFlags);
const error = computed(() => store.state.onboarding.error);
const domain = computed(() => store.state.onboarding.domain);
const isExtractionPending = computed(
  () => store.getters['onboarding/isExtractionPending']
);
const isExtractionFailed = computed(
  () => store.getters['onboarding/isExtractionFailed']
);
const lindexStatus = computed(() => store.state.onboarding.lindexStatus);

function shouldShowExtraction() {
  if (!domain.value) return false;
  if (isExtractionPending.value) return true;
  if (['pending', 'processing'].includes(lindexStatus.value)) return true;
  if (isExtractionFailed.value && view.value === 'extracting') return true;
  return false;
}

if (shouldShowExtraction()) {
  view.value = 'extracting';
}

watch([domain, isExtractionPending, lindexStatus], () => {
  if (view.value === 'form' && shouldShowExtraction()) {
    view.value = 'extracting';
  }
});

const submit = async () => {
  if (!spacesUrl.value.trim() || !urlIsValid.value) return;
  await store.dispatch('onboarding/start', spacesUrl.value.trim());
  view.value = 'extracting';
};

const backToForm = () => {
  view.value = 'form';
};

const continueToChannel = async () => {
  await store.dispatch('onboarding/continueAfterExtraction');
};

function registerFormNav() {
  nav?.registerNav({
    primary: {
      label: t('ONBOARDING_WIZARD.WELCOME.CTA'),
      disabled: !urlIsValid.value,
      loading: uiFlags.value.isStarting,
      onClick: submit,
    },
  });
}

watch(
  [view, urlIsValid, () => uiFlags.value.isStarting],
  () => {
    if (view.value === 'form') registerFormNav();
  },
  { immediate: true }
);

onMounted(() => {
  if (view.value === 'form') registerFormNav();
});

onBeforeUnmount(() => {
  nav?.clearNav();
});
</script>

<template>
  <OnboardingExtractionProgress
    v-if="view === 'extracting'"
    @back="backToForm"
    @complete="continueToChannel"
  />
  <OnboardingStepLayout
    v-else
    :step-label="t('ONBOARDING_WIZARD.PHASES.1.LABEL')"
    :title="t('ONBOARDING_WIZARD.WELCOME.TITLE')"
    :description="t('ONBOARDING_WIZARD.WELCOME.DESCRIPTION')"
  >
    <form
      id="onboarding-welcome-form"
      class="w-full flex flex-col gap-5"
      @submit.prevent="submit"
    >
      <UrlCheckInput
        v-model="spacesUrl"
        :label="t('ONBOARDING_WIZARD.WELCOME.SPACES_URL_LABEL')"
        :placeholder="t('ONBOARDING_WIZARD.WELCOME.SPACES_URL_PLACEHOLDER')"
        :hint="t('ONBOARDING_WIZARD.WELCOME.SPACES_URL_HINT')"
        @validity-change="urlIsValid = $event"
      />
      <p v-if="error" class="text-sm text-red-600 dark:text-red-400 m-0">
        {{ error }}
      </p>
    </form>
  </OnboardingStepLayout>
</template>
